import React, {useEffect, useState} from 'react';
import {Controller} from "react-hook-form";
import debounce from "lodash/debounce";
import {CheckBox, CheckBoxOutlineBlank} from "@mui/icons-material";
import {Autocomplete, Checkbox, Chip, TextField} from "@mui/material";
import {trafficSourceSelectPaperStyles} from "./TrafficSourceDesign";
import {customEventServices} from "../../service/customEventService";

const icon = <CheckBoxOutlineBlank fontSize="small"/>;
const checkedIcon = <CheckBox fontSize="small"/>;

function ReactHookCustomConversionMultiSelect(
    {
        control,
        inputLabel = "Select custom conversions",
        inputName = "selectedCustomConversions",
        updateOnChange = () => null,
        errors
    }
) {

    const selectId = React.useId();
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        async function fetchData(): void {
            try {
                setLoading(true);
                const response = await customEventServices.fetch({});
                setOptions(
                    response?.map(item => {
                        return {
                            id: item.id,
                            name: item?.name
                        };
                    })
                );
            } catch (e) {
                console.log("Error: ", e?.response?.data?.error);
            } finally {
                setLoading(false);
            }
        }

        if (open && (search.length === 0 || search.length > 0)) {
            fetchData();
        }

    }, [search, open]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
            setSearch("");
        }
    }, [open]);

    const handleSearch = debounce(searchTerm => {
        setSearch(searchTerm);
    }, 800);

    return (
        <Controller
            render={({field: {onChange, value, ...props}}) => (
                <Autocomplete
                    multiple
                    disableCloseOnSelect={true}
                    id={selectId}
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    isOptionEqualToValue={(option, value) => option?.name === value?.name}
                    getOptionLabel={(option) => option?.name || ""}
                    options={options}
                    loading={loading}
                    fullWidth
                    slotProps={{paper: {sx: trafficSourceSelectPaperStyles}}}
                    value={Array.isArray(value) ? value : []}
                    renderOption={(props, option, {selected}) => {
                        const {key, ...optionProps} = props;
                        return (
                        <li {...optionProps} key={key}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                sx={{marginRight: "8px", padding: "2px"}}
                                checked={selected}
                                color="primary"
                            />
                            {option?.name}
                        </li>
                    );}}
                    renderValue={(selected, getTagProps) =>
                        selected?.map((option, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            return (
                                <Chip
                                    key={key} variant="outlined" color="primary" size="small"
                                    label={option?.name} {...tagProps}
                                />);
                        })
                    }
                    renderInput={params => (
                        <TextField {...params} error={!!errors?.[inputName]} variant="outlined" size="small" fullWidth
                            helperText={errors?.[inputName] ? 'Choose a custom conversion.' : undefined}
                            placeholder="Select custom conversions" label={inputLabel}
                            onChange={e => handleSearch(e.target.value)} slotProps={{inputLabel: {shrink: true}}}/>
                    )}
                    onChange={(e, data) => {
                        onChange(data);
                        updateOnChange();
                    }}
                    {...props}
                />
            )}
            rules={{required: true}}
            defaultValue={[]}
            name={inputName}
            control={control}
            onChange={([, data]) => data}
        />
    );
}

export default ReactHookCustomConversionMultiSelect;